import { createFileRoute } from "@tanstack/react-router"
import { useQuery } from "convex/react"
import { motion, useReducedMotion } from "framer-motion"
import { api } from "../../../../convex/_generated/api"

export const Route = createFileRoute("/_authed/admin/analytics")({
  component: AnalyticsPage,
})

function AnalyticsPage() {
  const data = useQuery(api.analytics.systemAnalytics)

  if (data === undefined) {
    return <AnalyticsSkeleton />
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Analytics</h1>
        <p className="mt-1 text-sm text-[color:var(--muted-foreground)]">
          Cross-game aggregations, computed at query time. Light enough to
          recompute on every load — if it gets heavy we'll bake into a
          materialized view.
        </p>
      </div>

      <section>
        <h2 className="mb-2 text-sm font-semibold">Totals</h2>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          <Stat label="Events" value={data.totals.events} index={0} />
          <Stat label="Games" value={data.totals.games} index={1} />
          <Stat label="Venues" value={data.totals.venues} index={2} />
          <Stat label="Teams" value={data.totals.teams} index={3} />
          <Stat label="Rounds" value={data.totals.rounds} index={4} />
          <Stat label="Scores" value={data.totals.scores} index={5} />
        </div>
      </section>

      <section>
        <h2 className="mb-2 text-sm font-semibold">Games by status</h2>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
          <Stat label="Scheduled" value={data.gamesByStatus.scheduled} index={0} />
          <Stat label="Ready" value={data.gamesByStatus.ready} index={1} />
          <Stat label="Active" value={data.gamesByStatus.active} index={2} />
          <Stat label="Completed" value={data.gamesByStatus.completed} index={3} />
          <Stat label="Cancelled" value={data.gamesByStatus.cancelled} index={4} />
        </div>
      </section>

      <section>
        <h2 className="mb-2 text-sm font-semibold">Averages</h2>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat label="Teams per game" value={data.averages.teamsPerGame} index={0} />
          <Stat label="Rounds per game" value={data.averages.roundsPerGame} index={1} />
        </div>
      </section>

      <section>
        <h2 className="mb-2 text-sm font-semibold">Games by month</h2>
        {data.gamesByMonth.length === 0 ? (
          <EmptyHint label="No games yet." />
        ) : (
          <BarList
            rows={data.gamesByMonth.map((r) => ({
              label: r.month,
              count: r.count,
            }))}
          />
        )}
      </section>

      <div className="grid gap-6 sm:grid-cols-2">
        <section>
          <h2 className="mb-2 text-sm font-semibold">Top venues</h2>
          {data.gamesByVenue.length === 0 ? (
            <EmptyHint label="No venues yet." />
          ) : (
            <BarList
              rows={data.gamesByVenue.map((r) => ({
                label: r.name,
                count: r.count,
              }))}
            />
          )}
        </section>

        <section>
          <h2 className="mb-2 text-sm font-semibold">Decade popularity</h2>
          {data.decadePopularity.length === 0 ? (
            <EmptyHint label="No rounds yet." />
          ) : (
            <BarList
              rows={data.decadePopularity.map((r) => ({
                label: r.decade,
                count: r.count,
              }))}
            />
          )}
        </section>
      </div>

      <section>
        <h2 className="mb-2 text-sm font-semibold">Top teams (all-time)</h2>
        {data.topTeams.length === 0 ? (
          <EmptyHint label="No scored teams yet." />
        ) : (
          <ul className="divide-y divide-[color:var(--border)] overflow-hidden rounded-md border border-[color:var(--border)] bg-[color:var(--background)] text-sm">
            {data.topTeams.map((t, i) => (
              <TopTeamRow
                key={`${t.name}-${i}`}
                index={i}
                name={t.name}
                gameCount={t.gameCount}
                total={t.total}
              />
            ))}
          </ul>
        )}
      </section>
    </div>
  )
}

function Stat({
  label,
  value,
  index,
}: {
  label: string
  value: number | string
  index: number
}) {
  const reduce = useReducedMotion()
  return (
    <motion.div
      initial={{ opacity: 0, y: reduce ? 0 : 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: reduce ? 0 : 0.22,
        delay: reduce ? 0 : index * 0.025,
        ease: "easeOut",
      }}
      className="rounded-md border border-[color:var(--border)] bg-[color:var(--background)] p-3 transition-colors duration-150 hover:border-primary/40"
    >
      <p className="text-[10px] uppercase tracking-wide text-[color:var(--muted-foreground)]">
        {label}
      </p>
      <p className="mt-1 font-mono text-2xl font-semibold tabular-nums">
        {value}
      </p>
    </motion.div>
  )
}

function EmptyHint({ label }: { label: string }) {
  return (
    <p className="rounded-md border border-dashed border-[color:var(--border)] bg-[color:var(--muted)]/30 px-3 py-4 text-center text-xs text-[color:var(--muted-foreground)]">
      {label}
    </p>
  )
}

/**
 * Tiny bar list — proportional bars for a small set of categorical counts.
 * Cheap alternative to a real chart library; readable at trivia scales.
 *
 * Bars animate in with a quick spring on first render, staggered top-down so
 * the eye reads ranking order naturally.
 */
function BarList({ rows }: { rows: Array<{ label: string; count: number }> }) {
  const reduce = useReducedMotion()
  const max = Math.max(1, ...rows.map((r) => r.count))
  return (
    <ul className="space-y-1.5 text-sm">
      {rows.map((r, i) => {
        const pct = (r.count / max) * 100
        return (
          <motion.li
            key={r.label}
            initial={{ opacity: 0, x: reduce ? 0 : -4 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{
              duration: reduce ? 0 : 0.18,
              delay: reduce ? 0 : i * 0.04,
              ease: "easeOut",
            }}
            className="group flex items-center gap-3"
          >
            <span
              className="w-24 truncate text-[color:var(--muted-foreground)]"
              title={r.label}
            >
              {r.label}
            </span>
            <span className="relative h-5 flex-1 overflow-hidden rounded bg-[color:var(--muted)]">
              <motion.span
                initial={{ width: reduce ? `${pct}%` : "0%" }}
                animate={{ width: `${pct}%` }}
                transition={
                  reduce
                    ? { duration: 0 }
                    : {
                        type: "spring",
                        stiffness: 180,
                        damping: 24,
                        delay: 0.06 + i * 0.04,
                      }
                }
                className="absolute inset-y-0 left-0 rounded-r bg-primary transition-[filter] duration-150 group-hover:brightness-110"
              />
            </span>
            <span className="w-10 text-right font-mono text-xs tabular-nums">
              {r.count}
            </span>
          </motion.li>
        )
      })}
    </ul>
  )
}

function TopTeamRow({
  index,
  name,
  gameCount,
  total,
}: {
  index: number
  name: string
  gameCount: number
  total: number
}) {
  const reduce = useReducedMotion()
  const isLeader = index === 0
  return (
    <motion.li
      initial={{ opacity: 0, y: reduce ? 0 : 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: reduce ? 0 : 0.18,
        delay: reduce ? 0 : index * 0.03,
        ease: "easeOut",
      }}
      className={`flex items-center justify-between gap-2 px-4 py-2 transition-colors duration-150 hover:bg-[color:var(--muted)]/50 ${
        isLeader ? "bg-amber-50/60" : ""
      }`}
    >
      <span className="inline-flex items-center gap-2">
        {isLeader ? (
          <span
            className="inline-flex h-4 w-4 items-center justify-center"
            aria-label="Top team"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-amber-500"
              aria-hidden
            >
              <circle cx="12" cy="14" r="6" fill="currentColor" stroke="none" />
              <path d="M8 4l2 5M16 4l-2 5" strokeWidth="1.8" />
            </svg>
          </span>
        ) : (
          <span className="inline-block w-4 text-right font-mono text-xs text-[color:var(--muted-foreground)]">
            {index + 1}
          </span>
        )}
        <span className="truncate">{name}</span>
      </span>
      <span className="flex items-center gap-3 text-xs text-[color:var(--muted-foreground)]">
        <span>
          {gameCount} game{gameCount === 1 ? "" : "s"}
        </span>
        <span className="font-mono text-base font-semibold tabular-nums text-[color:var(--foreground)]">
          {total}
        </span>
      </span>
    </motion.li>
  )
}

function AnalyticsSkeleton() {
  return (
    <div className="space-y-8">
      <div className="space-y-3">
        <div className="h-7 w-40 animate-pulse rounded bg-[color:var(--muted)]" />
        <div className="h-3 w-2/3 animate-pulse rounded bg-[color:var(--muted)]" />
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            className="h-16 animate-pulse rounded-md border border-[color:var(--border)] bg-[color:var(--muted)]/40"
          />
        ))}
      </div>
      <div className="space-y-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="flex items-center gap-3">
            <div className="h-3 w-24 animate-pulse rounded bg-[color:var(--muted)]" />
            <div className="h-5 flex-1 animate-pulse rounded bg-[color:var(--muted)]" />
            <div className="h-3 w-8 animate-pulse rounded bg-[color:var(--muted)]" />
          </div>
        ))}
      </div>
    </div>
  )
}
